/**
 * RRWeb Replay Component
 * Handles session replay using rrweb library
 */

class RrwebReplay {
    constructor(config = {}) {
        this.config = {
            sessionId: config.sessionId || 'default-session',
            container: config.container || document.body,
            rrwebConfig: config.rrwebConfig || {},
            events: config.events || [],
            ...config
        };

        this.replayInstance = null;
        this.container = this.config.container;
        this.events = this.config.events;
        this.sessionId = this.config.sessionId;
        this.isPlaying = false;
        this.currentTime = 0;
        this.totalDuration = 0;
    }

    /**
     * Initialize the replay player
     */
    init() {
        if (!this.container) {
            console.error('No container specified for rrweb replay');
            return;
        }

        // Clear container
        this.container.innerHTML = '';

        // Create replay player
        this.replayInstance = new rrwebPlayer({
            target: this.container,
            props: {
                events: this.events,
                autoPlay: false,
                speed: this.config.rrwebConfig.speed || 1,
                showController: this.config.rrwebConfig.showController || true,
                showProgressBar: this.config.rrwebConfig.showProgressBar || true,
                showTimestamp: this.config.rrwebConfig.showTimestamp || true,
                showPlaybackRate: this.config.rrwebConfig.showPlaybackRate || true,
                showPauseButton: this.config.rrwebConfig.showPauseButton || true,
                showPlayButton: this.config.rrwebConfig.showPlayButton || true,
                showSkipButton: this.config.rrwebConfig.showSkipButton || true,
                showReplayButton: this.config.rrwebConfig.showReplayButton || true,
                showFullscreenButton: this.config.rrwebConfig.showFullscreenButton || true
            }
        });

        // Set up event listeners
        this.setupEventListeners();

        console.log(`RRWeb replay initialized for session ${this.sessionId}`);
    }

    /**
     * Set up event listeners for the replay player
     */
    setupEventListeners() {
        if (!this.replayInstance) return;

        // Listen to player events
        this.replayInstance.on('play', () => {
            this.isPlaying = true;
            console.log('Replay started');
        });

        this.replayInstance.on('pause', () => {
            this.isPlaying = false;
            console.log('Replay paused');
        });

        this.replayInstance.on('finish', () => {
            this.isPlaying = false;
            console.log('Replay finished');
        });

        this.replayInstance.on('timeupdate', (time) => {
            this.currentTime = time;
            // console.log('Time update:', time);
        });

        this.replayInstance.on('durationchange', (duration) => {
            this.totalDuration = duration;
            console.log('Duration changed:', duration);
        });
    }

    /**
     * Load events for replay
     */
    loadEvents(events) {
        this.events = events;
        if (this.replayInstance) {
            this.replayInstance.updateProps({
                events: events
            });
            console.log('Events loaded for replay');
        }
    }

    /**
     * Start playback
     */
    play() {
        if (this.replayInstance) {
            this.replayInstance.play();
            this.isPlaying = true;
        }
    }

    /**
     * Pause playback
     */
    pause() {
        if (this.replayInstance) {
            this.replayInstance.pause();
            this.isPlaying = false;
        }
    }

    /**
     * Restart playback from beginning
     */
    restart() {
        if (this.replayInstance) {
            this.replayInstance.restart();
            this.isPlaying = true;
        }
    }

    /**
     * Seek to specific time
     */
    seek(time) {
        if (this.replayInstance) {
            this.replayInstance.seek(time);
        }
    }

    /**
     * Set playback speed
     */
    setSpeed(speed) {
        if (this.replayInstance) {
            this.replayInstance.updateProps({
                speed: speed
            });
        }
    }

    /**
     * Get current state
     */
    getState() {
        return {
            isPlaying: this.isPlaying,
            currentTime: this.currentTime,
            totalDuration: this.totalDuration,
            sessionId: this.sessionId,
            eventCount: this.events.length
        };
    }

    /**
     * Destroy the replay instance
     */
    destroy() {
        if (this.replayInstance) {
            this.replayInstance.destroy();
            this.replayInstance = null;
        }
        console.log('RRWeb replay destroyed');
    }

    /**
     * Update configuration
     */
    updateConfig(config) {
        this.config = { ...this.config, ...config };
        if (this.replayInstance) {
            this.replayInstance.updateProps(config.rrwebConfig || {});
        }
    }
}

// ES module export
export { RrwebReplay };

// Export for global use
if (typeof window !== 'undefined') {
    window.RrwebReplay = RrwebReplay;
}